import { type FC, useState } from "react";

import { Button, Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

import type { EvalScoringDimension } from "@api/rest/types-and-hooks";
import { CriterionDialog } from "@components/EvalRubric/CriterionDialog";
import { EvalRubricsTable } from "@components/EvalRubric/EvalRubricsTable";
import { StandardInForce } from "@components/EvalRubric/StandardInForce";
import { useRefreshRubric } from "@components/EvalRubric/useRefreshRubric";

/**
 * Eval rubric index.
 *
 * A rubric says what each score *means* — the anchor text that pins what a 3
 * is, per dimension. It does not decide which dimensions are scored; that set
 * is fixed in code, because a rubric that could narrow it silently stopped
 * four of five dimensions being scored the moment someone authored one
 * criterion.
 *
 * Deliberately not per-organization, unlike QA criteria: if each org scored
 * against its own rubric, comparing models across orgs would be meaningless.
 *
 * Versions are frozen on activation and only one is active at a time. A score
 * records the version and content hash it was made against, so revisions are
 * new versions rather than edits.
 */
export const EvalRubrics: FC = () => {
  // What the dialog is about. Null means closed; `{}` means adding. The
  // dialog is mounted only while set and keyed on the subject, so fields
  // reset between subjects without an effect copying props into state.
  const [editing, setEditing] = useState<{ row?: EvalScoringDimension } | null>(null);
  const refreshRubric = useRefreshRubric();

  return (
    <div className="flex min-h-full flex-col">
      <div className="shrink-0 px-16 pt-8">
        {/* Reached from the Evaluations page rather than the sidebar, so the
            way back has to be on the page. */}
      </div>

      <div className="flex shrink-0 items-end justify-between px-16 pb-4 pt-2">
        <div>
          <Headline size="medium">Eval Rubric</Headline>
          <Paragraph displayType="normal" size="small" className="mt-1 text-text-secondary">
            What every AI output is scored on, and what each score from 1 to 5 means. Editing a criterion saves a new
            version, so past scores stay tied to the wording they were made against.
          </Paragraph>
        </div>
        <Button text="New criteria" appearance="primary" className="rounded-sm" onClick={() => setEditing({})} />
      </div>

      <div className="flex w-full flex-col gap-y-6 overflow-hidden px-16">
        <StandardInForce onEdit={(row) => setEditing({ row })} />

        <EvalRubricsTable />
      </div>

      {editing ? (
        <CriterionDialog
          key={editing.row?.dimension ?? "new"}
          open
          row={editing.row}
          onClose={() => setEditing(null)}
          onSaved={refreshRubric}
        />
      ) : null}
    </div>
  );
};
